import React, { useState } from 'react';
import { Mail, Phone, MapPin, Building2, CheckCircle2, AlertCircle } from 'lucide-react';
import { useLanguage } from '../i18n/LanguageContext';
import SEO from '../components/SEO';

export default function Contact() {
  const { t } = useLanguage();
  const page = t.contactPage;
  const contact = t.contactInfo;
  const [formStatus, setFormStatus] = useState('idle');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setFormStatus('submitting');
    
    const formData = new FormData(e.target);
    const data = Object.fromEntries(formData.entries());

    try {
      const response = await fetch('https://api.dev.auxo.dk/webform/submit', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(data),
      });

      if (response.ok) {
        setFormStatus('success');
        e.target.reset();
      } else {
        setFormStatus('error');
      }
    } catch {
      setFormStatus('error');
    }
  };

  return (
    <main className="bg-stone-50 pt-32 min-h-screen">
      <SEO pageKey="contact" />
      
      {/* Header */}
      <section className="py-20 md:py-28 bg-[#f7f2ea] text-stone-900 border-b border-stone-200/80">
        <div className="grid-container">
          <div className="max-w-4xl">
            <span className="text-xs font-bold uppercase tracking-[0.4em] text-[#581c24] mb-3 block">
              {page.badge}
            </span>
            <h1 className="text-5xl md:text-7xl font-serif font-bold uppercase leading-tight tracking-tight mb-6 text-stone-950">
              {page.heroTitle1} <br /> 
              <span className="text-[#581c24] italic font-light">{page.heroTitle2}</span>
            </h1>
            <p className="text-base md:text-lg text-stone-700 max-w-xl leading-relaxed">
              {page.heroSubtitle}
            </p>
          </div>
        </div>
      </section>

      {/* Form & Info Section */}
      <section className="relative -mt-20 pb-32">
        <div className="grid-container">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-start">
            
            {/* Left: Contact Info */}
            <div className="lg:col-span-5 space-y-12 py-12">
              <div className="space-y-8">
                <h2 className="text-2xl font-serif font-bold uppercase tracking-tighter text-stone-950">
                  {page.infoTitle}
                </h2>
                <div className="space-y-6">
                  <div className="flex items-start space-x-5">
                    <div className="w-12 h-12 bg-white rounded-xl shadow-xs border border-stone-200/80 flex items-center justify-center text-[#581c24] shrink-0">
                      <Mail className="w-5 h-5" />
                    </div>
                    <div>
                      <h4 className="text-[10px] font-bold uppercase tracking-[0.2em] text-stone-500 mb-1">
                        {page.emailLabel}
                      </h4>
                      <a href={`mailto:${contact.email}`} className="text-stone-950 font-medium hover:text-[#581c24] transition-colors">
                        {contact.email}
                      </a>
                    </div>
                  </div>

                  <div className="flex items-start space-x-5">
                    <div className="w-12 h-12 bg-white rounded-xl shadow-xs border border-stone-200/80 flex items-center justify-center text-[#581c24] shrink-0">
                      <Phone className="w-5 h-5" />
                    </div>
                    <div>
                      <h4 className="text-[10px] font-bold uppercase tracking-[0.2em] text-stone-500 mb-1">
                        {page.phoneLabel}
                      </h4>
                      <a href={`tel:${contact.phone}`} className="text-stone-950 font-medium hover:text-[#581c24] transition-colors">
                        {contact.phone}
                      </a>
                    </div>
                  </div>

                  <div className="flex items-start space-x-5">
                    <div className="w-12 h-12 bg-white rounded-xl shadow-xs border border-stone-200/80 flex items-center justify-center text-[#581c24] shrink-0">
                      <MapPin className="w-5 h-5" />
                    </div>
                    <div>
                      <h4 className="text-[10px] font-bold uppercase tracking-[0.2em] text-stone-500 mb-1">
                        {page.addressLabel}
                      </h4>
                      <p className="text-stone-950 font-medium">{contact.address}</p>
                    </div>
                  </div>

                  <div className="flex items-start space-x-5">
                    <div className="w-12 h-12 bg-white rounded-xl shadow-xs border border-stone-200/80 flex items-center justify-center text-[#581c24] shrink-0">
                      <Building2 className="w-5 h-5" />
                    </div>
                    <div>
                      <h4 className="text-[10px] font-bold uppercase tracking-[0.2em] text-stone-500 mb-1">
                        {page.cvrLabel}
                      </h4>
                      <p className="text-stone-950 font-medium">{contact.cvr}</p>
                    </div>
                  </div>
                </div>
              </div>

              <div className="p-8 bg-[#f4efe6] rounded-xl border border-stone-300/80 text-stone-900 shadow-sm">
                <span className="text-3xl font-serif font-bold text-[#581c24] italic block mb-2">
                  {page.experienceBoxNumber}
                </span>
                <p className="text-xs font-bold uppercase tracking-[0.2em] text-stone-700">
                  {page.experienceBoxText}
                </p>
              </div>
            </div>

            {/* Right: Contact Form Card */}
            <div className="lg:col-span-7">
              <div className="bg-white p-8 md:p-12 rounded-2xl shadow-xl relative z-10 border border-stone-200/90">
                <h3 className="text-2xl font-serif font-bold uppercase tracking-tight text-stone-950 mb-8">
                  {page.formTitle}
                </h3>
                
                {formStatus === 'success' ? (
                  <div className="p-8 bg-emerald-50 rounded-xl border border-emerald-200 text-center space-y-4">
                    <CheckCircle2 className="w-12 h-12 text-emerald-600 mx-auto" />
                    <h4 className="text-lg font-serif font-bold text-emerald-950">
                      {page.formSuccess}
                    </h4>
                    <button 
                      onClick={() => setFormStatus('idle')}
                      className="btn-secondary text-xs uppercase tracking-widest"
                    >
                      {page.formSubmit}
                    </button>
                  </div>
                ) : (
                  <form onSubmit={handleSubmit} className="space-y-6">
                    {/* Antispam Honeypot */}
                    <div className="sr-only absolute -z-50 opacity-0 pointer-events-none">
                      <label htmlFor="website_url">Website URL</label>
                      <input type="text" id="website_url" name="website_url" tabIndex="-1" autoComplete="off" />
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div className="space-y-2">
                        <label className="text-[10px] font-bold uppercase tracking-[0.2em] text-stone-500">
                          {t.common.name} *
                        </label>
                        <input 
                          type="text" 
                          name="name" 
                          placeholder={page.formName}
                          required
                          className="w-full bg-[#faf8f5] border border-stone-200 rounded-xl px-4 py-3 text-sm focus:border-[#581c24] focus:bg-white outline-none transition-all"
                        />
                      </div>

                      <div className="space-y-2">
                        <label className="text-[10px] font-bold uppercase tracking-[0.2em] text-stone-500">
                          {t.common.email} *
                        </label>
                        <input 
                          type="email" 
                          name="email" 
                          placeholder={page.formEmail}
                          required
                          className="w-full bg-[#faf8f5] border border-stone-200 rounded-xl px-4 py-3 text-sm focus:border-[#581c24] focus:bg-white outline-none transition-all"
                        />
                      </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div className="space-y-2">
                        <label className="text-[10px] font-bold uppercase tracking-[0.2em] text-stone-500">
                          {t.common.phone}
                        </label>
                        <input 
                          type="tel" 
                          name="phone" 
                          placeholder="+45 -- -- -- --"
                          className="w-full bg-[#faf8f5] border border-stone-200 rounded-xl px-4 py-3 text-sm focus:border-[#581c24] focus:bg-white outline-none transition-all"
                        />
                      </div>

                      <div className="space-y-2">
                        <label className="text-[10px] font-bold uppercase tracking-[0.2em] text-stone-500">
                          {t.common.subject}
                        </label>
                        <select 
                          name="subject" 
                          className="w-full bg-[#faf8f5] border border-stone-200 rounded-xl px-4 py-3 text-sm focus:border-[#581c24] focus:bg-white outline-none transition-all"
                        >
                          {page.formOptions.map((opt, i) => (
                            <option key={i} value={opt}>{opt}</option>
                          ))}
                        </select>
                      </div>
                    </div>

                    <div className="space-y-2">
                      <label className="text-[10px] font-bold uppercase tracking-[0.2em] text-stone-500">
                        {t.common.message} *
                      </label>
                      <textarea 
                        name="message" 
                        rows="5"
                        placeholder={page.formMessage}
                        required
                        className="w-full bg-[#faf8f5] border border-stone-200 rounded-xl p-4 text-sm focus:border-[#581c24] focus:bg-white outline-none transition-all resize-none"
                      ></textarea>
                    </div>

                    {formStatus === 'error' && (
                      <div className="p-4 bg-rose-50 border border-rose-200 rounded-xl flex items-center space-x-3 text-rose-800 text-xs">
                        <AlertCircle className="w-5 h-5 shrink-0" />
                        <span>{page.formError}</span>
                      </div>
                    )}

                    <button 
                      type="submit" 
                      disabled={formStatus === 'submitting'}
                      className="w-full btn-primary justify-center py-4 rounded-xl shadow-md disabled:opacity-50"
                    >
                      {formStatus === 'submitting' ? page.formSubmitting : page.formSubmit}
                    </button>
                  </form>
                )}
              </div>
            </div>

          </div>
        </div>
      </section>

    </main>
  );
}
